/* FlexibleSkeleton Component Styles */
.flexibleSkeleton {
  position: relative;
}

.skeletonItem {
  position: relative;
  overflow: hidden;
}

/* Animation Classes */
@keyframes wave {
  0% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

@keyframes shimmer {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}

.waveAnimation {
  position: relative;
  background-color: var(--mantine-color-gray-1);
}

.waveAnimation::after {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.6),
    transparent
  );
  animation: wave 1.6s infinite;
  content: "";
}

.pulseAnimation {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.shimmerAnimation {
  background: linear-gradient(
    90deg,
    var(--mantine-color-gray-1) 25%,
    var(--mantine-color-gray-0) 37%,
    var(--mantine-color-gray-1) 63%
  );
  background-size: 400% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

/* Responsive utilities */
@media (max-width: 480px) {
  .flexibleSkeleton {
    gap: 4px;
  }

  .skeletonItem {
    min-height: 12px;
  }
}

@media (min-width: 481px) and (max-width: 768px) {
  .flexibleSkeleton {
    gap: 6px;
  }
}

@media (min-width: 769px) {
  .flexibleSkeleton {
    gap: 8px;
  }
}

/* Accessibility improvements */
.flexibleSkeleton[aria-busy="true"] {
  pointer-events: none;
}

.flexibleSkeleton[role="status"] {
  position: relative;
}

.flexibleSkeleton[role="status"]::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: -1;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .skeletonItem {
    border: 1px solid currentColor;
  }

  .waveAnimation::after {
    background: linear-gradient(90deg, transparent, currentColor, transparent);
    opacity: 0.3;
  }

  .shimmerAnimation {
    background: linear-gradient(
      90deg,
      transparent 25%,
      currentColor 37%,
      transparent 63%
    );
    opacity: 0.2;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .waveAnimation::after,
  .pulseAnimation,
  .shimmerAnimation {
    animation: none;
  }

  .flexibleSkeleton {
    transition: none;
  }
}

/* Dark mode support */
[data-mantine-color-scheme="dark"] .waveAnimation {
  background-color: var(--mantine-color-dark-6);
}

[data-mantine-color-scheme="dark"] .waveAnimation::after {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
}

[data-mantine-color-scheme="dark"] .shimmerAnimation {
  background: linear-gradient(
    90deg,
    var(--mantine-color-dark-6) 25%,
    var(--mantine-color-dark-5) 37%,
    var(--mantine-color-dark-6) 63%
  );
}

/* Loading state indicators */
.flexibleSkeleton[data-loading="true"] {
  cursor: wait;
}

@media (prefers-reduced-motion: reduce) {
  .flexibleSkeleton:hover .skeletonItem {
    transform: none;
    transition: none;
  }
}

/* Focus indicators for accessibility */
.flexibleSkeleton:focus-within {
  outline: 2px solid var(--mantine-color-blue-6);
  outline-offset: 2px;
  border-radius: var(--mantine-radius-sm);
}

/* Custom variant styles */
.flexibleSkeleton[data-variant="card"] .skeletonItem {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: var(--mantine-radius-md);
}

.flexibleSkeleton[data-variant="text"] .skeletonItem {
  border-radius: var(--mantine-radius-xs);
}

.flexibleSkeleton[data-variant="button"] .skeletonItem {
  border-radius: var(--mantine-radius-sm);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.flexibleSkeleton[data-variant="input"] .skeletonItem {
  border: 1px solid var(--mantine-color-gray-3);
  border-radius: var(--mantine-radius-sm);
}

/* Performance optimizations */
.skeletonItem {
  will-change: opacity, transform;
  backface-visibility: hidden;
  perspective: 1000px;
}

.waveAnimation::after {
  will-change: transform;
  backface-visibility: hidden;
}

/* Print styles */
@media print {
  .flexibleSkeleton {
    display: none;
  }
}
